import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Calendar, Clock, User, ChevronRight, Search, Tag } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';

const API_BASE = window.location.origin || '';

// Helper function to get full image URL
const getImageUrl = (url) => {
  if (!url) return '';
  
  // Fix old domain references
  let fixedUrl = url;
  if (url.includes('vipgez-premium.emergent.host')) {
    fixedUrl = url.replace('https://vipgez-premium.emergent.host', 'https://goturkeytransfer.com');
  }
  
  // If already absolute URL, return the fixed version
  if (fixedUrl.startsWith('http://') || fixedUrl.startsWith('https://')) return fixedUrl;
  // If relative URL, prepend API_BASE
  return `${API_BASE}${fixedUrl.startsWith('/') ? fixedUrl : '/' + fixedUrl}`;
};

const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchPosts();
    fetchCategories();
  }, [currentPage, selectedCategory]);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '9'
      });
      if (selectedCategory) {
        params.set('category', selectedCategory);
      }

      const response = await fetch(`${API_BASE}/api/cms/blog/posts?${params}`);
      const data = await response.json();
      
      if (data.success) {
        setPosts(data.posts);
        setTotalPages(data.totalPages);
      }
    } catch (error) {
      console.error('Failed to fetch blog posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/cms/blog/categories`);
      const data = await response.json();
      if (data.success) {
        setCategories(data.categories);
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // FAQ data for Blog FAQ schema
  const faqData = [
    {
      question: "What are the best airports in Turkey for tourists?",
      answer: "The main airports for tourists are Istanbul Airport (IST), Antalya Airport (AYT), Dalaman Airport (DLM), and Bodrum Airport (BJV). Each serves different regions of Turkey."
    },
    {
      question: "How do I book an airport transfer in Turkey?",
      answer: "You can book a VIP airport transfer through GoTurkeyTransfer.com. Simply enter your pickup and drop-off locations, select your vehicle, and complete the booking."
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
        
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 pt-32 md:pt-36 pb-16">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMiIvPjwvZz48L2c+PC9zdmc+')] opacity-50" />
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
                GoTurkeyTransfer <span className="text-primary">Blog</span>
              </h1>
              <p className="text-xl text-gray-300 mb-8">
                Travel tips, destination guides, and expert advice for your Turkey journey
              </p>
              
              {/* Search Bar */}
              <div className="relative max-w-md mx-auto">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 pr-4 py-3 bg-white/10 border-white/20 text-white placeholder:text-gray-400 focus:bg-white/20"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Categories Filter */}
        {categories.length > 0 && (
          <section className="bg-secondary/50 border-b border-border">
            <div className="container mx-auto px-4 py-4">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm text-muted-foreground font-medium">Categories:</span>
                <Button
                  variant={selectedCategory === '' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => {
                    setSelectedCategory('');
                    setCurrentPage(1);
                  }}
                  className={selectedCategory === '' ? 'bg-primary text-primary-foreground' : ''}
                >
                  All
                </Button>
                {categories.map((cat) => (
                  <Button
                    key={cat.name}
                    variant={selectedCategory === cat.name ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => {
                      setSelectedCategory(cat.name);
                      setCurrentPage(1);
                    }}
                    className={selectedCategory === cat.name ? 'bg-primary text-primary-foreground' : ''}
                  >
                    {cat.name} ({cat.count})
                  </Button>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Blog Posts Grid */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="bg-secondary rounded-xl h-48 mb-4" />
                    <div className="bg-secondary rounded h-6 w-3/4 mb-2" />
                    <div className="bg-secondary rounded h-4 w-full mb-2" />
                    <div className="bg-secondary rounded h-4 w-2/3" />
                  </div>
                ))}
              </div>
            ) : posts.length === 0 ? (
              <div className="text-center py-20">
                <div className="text-6xl mb-4">📝</div>
                <h2 className="text-2xl font-semibold text-foreground mb-2">No blog posts yet</h2>
                <p className="text-muted-foreground">Check back soon for travel tips and guides!</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {posts.map((post) => (
                    <article 
                      key={post.id} 
                      className="group bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden hover:border-primary/50 transition-all duration-300 hover:shadow-lg hover:shadow-primary/5"
                    >
                      {/* Cover Image */}
                      <Link to={post.url} className="block relative overflow-hidden">
                        {post.coverImage ? (
                          <img
                            src={getImageUrl(post.coverImage)}
                            alt={post.title}
                            className="w-full h-52 object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        ) : (
                          <div className="w-full h-52 bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
                            <span className="text-6xl">✈️</span>
                          </div>
                        )}
                        {post.category && (
                          <span className="absolute top-4 left-4 px-3 py-1 bg-primary text-primary-foreground text-xs font-medium rounded-full">
                            {post.category}
                          </span>
                        )}
                      </Link>
                      
                      {/* Content */}
                      <div className="p-6">
                        {/* Meta Info */}
                        <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400 mb-3">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            {formatDate(post.publishedDate)}
                          </span>
                          {post.readTime && (
                            <span className="flex items-center gap-1">
                              <Clock className="h-4 w-4" />
                              {post.readTime}
                            </span>
                          )}
                        </div>
                        
                        {/* Title */}
                        <Link to={post.url}>
                          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 group-hover:text-primary transition-colors line-clamp-2">
                            {post.title}
                          </h2>
                        </Link>
                        
                        {/* Excerpt */}
                        <p className="text-gray-600 dark:text-gray-300 text-sm line-clamp-3 mb-4">
                          {post.excerpt}
                        </p>
                        
                        {/* Footer */}
                        <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                          <span className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                            <User className="h-4 w-4" />
                            {post.author}
                          </span>
                          <Link 
                            to={post.url}
                            className="flex items-center gap-1 text-primary font-medium text-sm hover:gap-2 transition-all"
                          >
                            Read More
                            <ChevronRight className="h-4 w-4" />
                          </Link>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center items-center gap-2 mt-12">
                    <Button
                      variant="outline"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </Button>
                    
                    {[...Array(totalPages)].map((_, i) => (
                      <Button
                        key={i + 1}
                        variant={currentPage === i + 1 ? 'default' : 'outline'}
                        onClick={() => setCurrentPage(i + 1)}
                        className={currentPage === i + 1 ? 'bg-primary' : ''}
                      >
                        {i + 1}
                      </Button>
                    ))}
                    
                    <Button
                      variant="outline"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-gradient-to-r from-primary/10 via-primary/5 to-primary/10 py-16">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
              Ready to Explore Turkey?
            </h2>
            <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
              Book your premium airport transfer and start your Turkish adventure with comfort and style.
            </p>
            <Link to="/">
              <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
                Book Your Transfer Now
              </Button>
            </Link>
          </div>
        </section>

        <Footer />
      </div>
  );
};

export default BlogPage;
